<template>
  <section class="space-y-6">
    <div class="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 class="text-lg font-semibold text-ink">Venue list</h2>
          <p class="text-sm text-muted">Manage vendor venues and pricing.</p>
        </div>
        <div class="flex flex-wrap gap-3">
          <button
            type="button"
            class="rounded-full border border-gray-200 px-4 py-2 text-xs font-semibold text-ink transition hover:border-brand hover:text-brand"
            :disabled="loading"
            @click="loadVenueRows"
          >
            {{ loading ? 'Refreshing...' : 'Refresh' }}
          </button>
          <RouterLink to="/dashboard" class="rounded-full bg-brand px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-brandDark">
            Manage businesses
          </RouterLink>
        </div>
      </div>

      <div v-if="error" class="mt-6 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
        {{ error }}
      </div>

      <div v-if="loading" class="mt-6 rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-sm text-muted">
        Loading venues...
      </div>

      <div v-else class="mt-6 overflow-hidden rounded-2xl border border-gray-100">
        <div class="grid grid-cols-6 gap-4 bg-gray-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted">
          <span>Venue</span>
          <span>Type</span>
          <span>Per day</span>
          <span>Spaces</span>
          <span>Business</span>
          <span></span>
        </div>
        <div class="divide-y divide-gray-100 text-sm">
          <div
            v-for="venue in venues"
            :key="venue.id"
            class="grid grid-cols-6 items-center gap-4 px-4 py-4"
          >
            <div>
              <p class="font-semibold text-ink">{{ venue.title }}</p>
              <p class="text-xs text-muted">{{ venue.address || 'Address not available' }}</p>
            </div>
            <span class="text-muted">{{ venue.type || 'Not set' }}</span>
            <span class="text-muted">{{ venue.priceLabel }}</span>
            <span class="text-muted">{{ venue.spaces }}</span>
            <span class="text-muted">{{ venue.businessName }}</span>
            <div class="flex items-center justify-end gap-3">
              <RouterLink
                :to="venue.detailLink"
                class="rounded-full border border-gray-200 px-3 py-1.5 text-xs font-semibold text-ink transition hover:border-brand hover:text-brand"
              >
                View
              </RouterLink>
              <ActionDock
                :open-label="`Show actions for ${venue.title}`"
                :close-label="`Close actions for ${venue.title}`"
                :edit-label="`Edit ${venue.title}`"
                :delete-label="`Delete ${venue.title}`"
                :deleting="deletingVenueId === String(venue.id)"
                @edit="router.push(venue.editLink)"
                @delete="deleteVenue(venue)"
              />
            </div>
          </div>
          <div v-if="!venues.length" class="px-4 py-6 text-center text-sm text-muted">No venues found for this vendor.</div>
        </div>
      </div>
    </div>

  </section>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import api from '@/services/vendor/api'
import { getAuthUserId } from '@/services/auth'
import ActionDock from '@/components/vendor/ActionDock.vue'

const router = useRouter()
const venues = ref([])
const loading = ref(false)
const error = ref('')
const deletingVenueId = ref('')

const normalizeVenue = (item = {}, business = {}) => {
  const id = String(item?.venue_id || item?.vendor_business_venue_id || item?.id || '').trim()
  const businessId = String(
    item?.business_id ||
    item?.vendor_business_id ||
    business?.id ||
    business?.business_id ||
    ''
  ).trim()
  const price = item?.price_per_day || item?.price || item?.min_price || item?.starting_price || ''

  return {
    id,
    businessId,
    title: item?.title || item?.name || 'Untitled venue',
    address: item?.address || item?.location || '',
    type: item?.type || item?.venue_type || item?.category || '',
    spaces: item?.spaces ?? item?.space_count ?? item?.total_spaces ?? 0,
    priceLabel: price ? String(price) : '-',
    businessName: business?.business_name || business?.name || business?.title || 'Business',
    detailLink: businessId && id ? `/business/${businessId}/venues/${id}` : '/dashboard',
    editLink: businessId && id ? `/dashboard/business/${businessId}/venue/${id}` : '/dashboard'
  }
}

const getBusinessItems = (data) => {
  const payload = data?.data || data || {}
  if (Array.isArray(payload)) return payload
  return payload?.vendors || payload?.business_vendors || payload?.businessVendor || payload?.items || payload?.list || []
}

const getVenueItems = (data) => {
  const payload = data?.data || data || {}
  if (Array.isArray(payload)) return payload
  return payload?.venues || payload?.items || payload?.list || payload?.venue_list || []
}

const loadVenueRows = async () => {
  loading.value = true
  error.value = ''
  venues.value = []

  try {
    const vendorId = getAuthUserId()
    if (!vendorId) {
      throw new Error('Vendor id is not available in auth storage.')
    }

    const businessFormData = new FormData()
    businessFormData.append('s_id', vendorId)
    const { data } = await api.listBusinessVendor(businessFormData)
    const businesses = getBusinessItems(data)

    if (!Array.isArray(businesses) || !businesses.length) {
      venues.value = []
      return
    }

    const responses = await Promise.all(
      businesses.map(async (business) => {
        const businessId = String(
          business?.id ||
          business?.business_id ||
          business?.business_vendor_id ||
          ''
        ).trim()

        if (!businessId) return []

        const venueFormData = new FormData()
        venueFormData.append('business_id', businessId)
        const venueResponse = await api.listVenuesBusinessVendor(venueFormData)
        const items = getVenueItems(venueResponse?.data)
        return Array.isArray(items) ? items.map((item) => normalizeVenue(item, business)) : []
      })
    )

    venues.value = responses.flat().filter((item) => item.id && item.businessId)
  } catch (err) {
    error.value = err?.response?.data?.msg || err?.response?.data?.message || err?.message || 'Failed to load venues'
    venues.value = []
  } finally {
    loading.value = false
  }
}

const deleteVenue = async (venue) => {
  const id = String(venue?.id || '').trim()
  if (!id) return

  deletingVenueId.value = id
  error.value = ''

  try {
    const payload = new FormData()
    payload.append('venue_id', id)
    const { data } = await api.deleteVenue(payload)
    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    if (isError) {
      throw new Error(data?.msg || data?.message || 'Failed to delete venue')
    }
    venues.value = venues.value.filter((item) => String(item.id) !== id)
  } catch (err) {
    error.value = err?.response?.data?.msg || err?.response?.data?.message || err?.message || 'Failed to delete venue'
  } finally {
    deletingVenueId.value = ''
  }
}

onMounted(() => {
  loadVenueRows()
})
</script>
